/**
 * SensoryPathwayCard — „Кой път страда" (Nerve2).
 * Вибрация и позиция (задни столбове) срещу убождане и температура
 * (спиноталамичен път) → находка + конкретният виновник от данните.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GitBranch, Loader2, CheckCircle2, Circle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIDE_BG = { left: "Ляво стъпало", right: "Дясно стъпало" };
const PATTERN_COLOR = {
  intact: "opacity-80",
  small_fiber: "text-amber-500",
  posterior_column: "text-amber-500",
  mixed: "text-rose-500",
};

export const SensoryPathwayCard = ({ d, setD, cardCls, border }) => {
  const proto = d?.pathway_protocols?.modality;
  const res = d?.sensory_pathways;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [vals, setVals] = useState({});

  if (!proto) return null;

  const start = () => {
    if (!open) {
      const init = {};
      proto.sides.forEach((s) => proto.tests.forEach((t) => {
        init[`${s}_${t.key}`] = true;
      }));
      setVals(init);
    }
    setOpen((v) => !v);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/neuro-twin/modality-exam`, { results: vals });
      setD(data);
      setOpen(false);
      toast.success("Прегледът по пътища е записан");
    } catch { toast.error("Записът не мина"); }
    finally { setBusy(false); }
  };

  return (
    <div className={cardCls} data-testid="neuro-pathway-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <GitBranch className="w-4 h-4 text-violet-500" />
            Кой път страда — преглед по модалности
          </div>
          {res ? (
            <>
              <p className={`text-[12px] font-bold mt-1 ${PATTERN_COLOR[res.pattern]}`}
                data-testid="neuro-pathway-pattern">
                {res.pattern_label_bg} ({res.date})
              </p>
              <p className="text-[12px] opacity-80 mt-1">{res.pattern_text_bg}</p>
              <p className="text-[12px] font-medium mt-1" data-testid="neuro-pathway-match">
                {res.match_bg}
              </p>
              {res.asymmetry_note_bg && (
                <p className="text-[12px] text-amber-500 mt-1" data-testid="neuro-pathway-asymmetry">
                  {res.asymmetry_note_bg}
                </p>
              )}
            </>
          ) : (
            <p className="text-[12px] opacity-75 mt-1">
              Още няма преглед. Вибрация, позиция на палеца, убождане и
              студено на двете стъпала — 5 минути, и се вижда дали страдат
              дебелите (B12) или тънките влакна (гликемия).
            </p>
          )}
        </div>
        <button type="button" onClick={start}
          className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold shrink-0"
          data-testid="neuro-pathway-toggle">
          {open ? "Затвори" : res ? "Нов преглед" : "Направи прегледа"}
        </button>
      </div>

      {res?.culprits?.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="neuro-pathway-culprits">
          {res.culprits.map((c) => (
            <div key={c.key} className={`p-2 rounded-lg border ${border}`}
              data-testid={`neuro-pathway-culprit-${c.key}`}>
              <div className="text-[12px] font-bold">{c.label_bg}</div>
              <div className="text-[11px] opacity-80">{c.why_bg}</div>
              <div className="text-[11px] font-semibold mt-0.5">{c.fits_bg}</div>
            </div>
          ))}
        </div>
      )}

      {res?.tracts_bg && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {["posterior", "spinothalamic"].map((k) => (
            <div key={k} className={`p-2 rounded-lg border ${border}`}
              data-testid={`neuro-tract-${k}`}>
              <div className="text-[11px] font-bold">{res.tracts_bg[k].name_bg}</div>
              <div className="text-[10px] opacity-60">носи: {res.tracts_bg[k].carries_bg}</div>
              <div className="text-[11px] opacity-80 mt-1">{res.tracts_bg[k].text_bg}</div>
              <div className="text-[10px] font-bold mt-1 opacity-70">
                неусетени: {res[k === "posterior" ? "posterior" : "spinothalamic"].missed}
                {" "}от {res[k === "posterior" ? "posterior" : "spinothalamic"].total}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="mt-3" data-testid="neuro-pathway-form">
          <ol className="text-[11px] opacity-80 space-y-1 list-decimal pl-4">
            {proto.steps_bg.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
          <div className="mt-3 space-y-2">
            {proto.tests.map((t) => (
              <div key={t.key} className={`p-2 rounded-lg border ${border}`}>
                <div className="text-[12px] font-bold">{t.label_bg}</div>
                <div className="text-[10px] opacity-70 mt-0.5">{t.how_bg}</div>
                <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                  {proto.sides.map((s) => {
                    const id = `${s}_${t.key}`;
                    const felt = vals[id] === true;
                    return (
                      <button key={id} type="button"
                        onClick={() => setVals((p) => ({ ...p, [id]: !felt }))}
                        className={`px-2 py-1 rounded-md text-[11px] text-left border transition-colors ${
                          felt ? "border-emerald-500/40 bg-emerald-500/10" : border}`}
                        data-testid={`neuro-modality-${id}`}>
                        {felt ? <CheckCircle2 className="w-3 h-3 inline mr-1" />
                              : <Circle className="w-3 h-3 inline mr-1 opacity-50" />}
                        {SIDE_BG[s]}: {felt ? "усещам" : "не усещам"}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <button type="button" onClick={save} disabled={busy}
            className="mt-3 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="neuro-pathway-save">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Запиши прегледа
          </button>
        </div>
      )}
    </div>
  );
};

export default SensoryPathwayCard;
